import React, { forwardRef, type CSSProperties } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import type { TextProps } from './text.types';

/**
 * Flatten array of styles into single CSSProperties object (mobile compatibility)
 */
const flattenStyle = (style?: CSSProperties | CSSProperties[]): CSSProperties | undefined => {
    if (!style) return undefined;
    if (Array.isArray(style)) {
        return Object.assign({}, ...style.filter(Boolean));
    }
    return style;
};

const textVariants = cva('', {
    variants: {
        variant: {
            h1: 'font-bold',
            h2: 'font-bold',
            h3: 'font-semibold',
            body: 'font-normal',
        },
        weight: {
            regular: 'font-normal',
            medium: 'font-medium',
            bold: 'font-bold',
        },
        // Size is defined here to enable type-safe variant props
        // Actual styles are applied via compound variants based on variant + size combination
        size: {
            xlarge: '',
            large: '',
            medium: '',
            small: '',
            xsmall: '',
            xxsmall: '',
        },
        align: {
            left: 'text-left',
            center: 'text-center',
            right: 'text-right',
            justify: 'text-justify',
        },
    },
    compoundVariants: [
        // H1 sizes
        { variant: 'h1', size: 'xlarge', className: 'text-6xl' },
        { variant: 'h1', size: 'large', className: 'text-5xl' },
        { variant: 'h1', size: 'medium', className: 'text-4xl' },
        { variant: 'h1', size: 'small', className: 'text-3xl' },
        { variant: 'h1', size: 'xsmall', className: 'text-2xl' },
        { variant: 'h1', size: 'xxsmall', className: 'text-xl' },

        // H2 sizes
        { variant: 'h2', size: 'xlarge', className: 'text-5xl' },
        { variant: 'h2', size: 'large', className: 'text-4xl' },
        { variant: 'h2', size: 'medium', className: 'text-3xl' },
        { variant: 'h2', size: 'small', className: 'text-2xl' },
        { variant: 'h2', size: 'xsmall', className: 'text-xl' },
        { variant: 'h2', size: 'xxsmall', className: 'text-lg' },

        // H3 sizes
        { variant: 'h3', size: 'xlarge', className: 'text-4xl' },
        { variant: 'h3', size: 'large', className: 'text-3xl' },
        { variant: 'h3', size: 'medium', className: 'text-2xl' },
        { variant: 'h3', size: 'small', className: 'text-xl' },
        { variant: 'h3', size: 'xsmall', className: 'text-lg' },
        { variant: 'h3', size: 'xxsmall', className: 'text-base' },

        // Body sizes
        { variant: 'body', size: 'xlarge', className: 'text-xl' },
        { variant: 'body', size: 'large', className: 'text-lg' },
        { variant: 'body', size: 'medium', className: 'text-base' },
        { variant: 'body', size: 'small', className: 'text-sm' },
        { variant: 'body', size: 'xsmall', className: 'text-xs' },
        { variant: 'body', size: 'xxsmall', className: 'text-2xs' },
    ],
    defaultVariants: {
        variant: 'body',
        weight: 'regular',
        size: 'medium',
        align: 'left',
    },
});

export interface TextComponentProps
    extends TextProps, Omit<VariantProps<typeof textVariants>, 'variant' | 'weight' | 'size' | 'align'> {}

const Text = forwardRef<HTMLElement, TextComponentProps>(
    (
        {
            className,
            variant = 'body',
            weight = 'regular',
            size = 'medium',
            align = 'left',
            children,
            as,
            showIf = true,
            testID,
            style,
            ...props
        },
        ref
    ) => {
        if (!showIf) return null;

        // Flatten style arrays (mobile compatibility)
        const flatStyle = flattenStyle(style);

        // Determine which HTML element to render
        // Default to 'span' for body variant to avoid hydration errors with nested <p> tags
        const getElement = () => {
            if (as) return as;
            switch (variant) {
                case 'h1':
                    return 'h1';
                case 'h2':
                    return 'h2';
                case 'h3':
                    return 'h3';
                default:
                    return 'span';
            }
        };

        const Element = getElement();

        return React.createElement(
            Element,
            {
                className: cn(textVariants({ variant, weight, size, align, className })),
                ref,
                style: flatStyle,
                'data-testid': testID,
                ...props,
            },
            children
        );
    }
);

Text.displayName = 'Text';

export default Text;
